import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';

interface RefOption {
  id: number;
  job_title?: string;
  unit_name?: string;
  location_name?: string;
  name?: string;
}

interface CoverageOption {
  id: number;
  option_code: string;
  option_name: string;
  coverage_amount: string | null;
  employee_cost: string;
  employer_cost: string;
}

interface EligibilityRule {
  id: number;
  job: RefOption | null;
  org_unit: RefOption | null;
  location: RefOption | null;
  pay_grade: RefOption | null;
  employment_type: string | null;
  minimum_fte: string | null;
  minimum_service_months: number | null;
  effective_from: string;
  effective_to: string | null;
}

interface BenefitPlanDetailRecord {
  id: number;
  plan_code: string;
  plan_name: string;
  effective_from: string;
  effective_to: string | null;
  active_flag: boolean;
  type: { type_name: string };
  provider: { provider_name: string };
  coverage_options: CoverageOption[];
  eligibility_rules: EligibilityRule[];
}

const TABS = ['details', 'coverage-options', 'eligibility-rules'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = { details: 'Details', 'coverage-options': 'Coverage Options', 'eligibility-rules': 'Eligibility Rules' };

const emptyOptionForm = { option_code: '', option_name: '', coverage_amount: '', employee_cost: '', employer_cost: '' };
const emptyRuleForm = {
  job_id: '', org_unit_id: '', location_id: '', pay_grade_id: '',
  employment_type: '', minimum_fte: '', minimum_service_months: '', effective_from: '', effective_to: '',
};

export function BenefitPlanDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [tab, setTab] = useState<Tab>('details');
  const [plan, setPlan] = useState<BenefitPlanDetailRecord | null>(null);
  const [jobs, setJobs] = useState<RefOption[]>([]);
  const [orgUnits, setOrgUnits] = useState<RefOption[]>([]);
  const [locations, setLocations] = useState<RefOption[]>([]);
  const [payGrades, setPayGrades] = useState<RefOption[]>([]);

  const [editingOption, setEditingOption] = useState<'new' | CoverageOption | null>(null);
  const [optionForm, setOptionForm] = useState(emptyOptionForm);
  const [optionErrors, setOptionErrors] = useState<Record<string, string[]>>({});

  const [editingRule, setEditingRule] = useState<'new' | EligibilityRule | null>(null);
  const [ruleForm, setRuleForm] = useState(emptyRuleForm);
  const [ruleErrors, setRuleErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<BenefitPlanDetailRecord>(`/employee-benefit-plans/${id}`).then((res) => setPlan(res.data));

  useEffect(() => {
    load();
    api.get<RefOption[]>('/jobs').then((res) => setJobs(res.data));
    api.get<RefOption[]>('/org-units').then((res) => setOrgUnits(res.data));
    api.get<RefOption[]>('/locations').then((res) => setLocations(res.data));
    api.get<RefOption[]>('/pay-grades').then((res) => setPayGrades(res.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openNewOption = () => {
    setOptionForm(emptyOptionForm);
    setOptionErrors({});
    setEditingOption('new');
  };

  const openEditOption = (option: CoverageOption) => {
    setOptionForm({
      option_code: option.option_code,
      option_name: option.option_name,
      coverage_amount: option.coverage_amount ?? '',
      employee_cost: option.employee_cost,
      employer_cost: option.employer_cost,
    });
    setOptionErrors({});
    setEditingOption(option);
  };

  const saveOption = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editingOption === 'new') await api.post(`/employee-benefit-plans/${id}/coverage-options`, optionForm);
      else if (editingOption) await api.put(`/employee-benefit-coverage-options/${editingOption.id}`, optionForm);
      setEditingOption(null);
      load();
    } catch (err) {
      setOptionErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeOption = async (option: CoverageOption) => {
    if (!(await confirmDelete(`Delete "${option.option_name}"?`))) return;
    try {
      await api.delete(`/employee-benefit-coverage-options/${option.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const openNewRule = () => {
    setRuleForm(emptyRuleForm);
    setRuleErrors({});
    setEditingRule('new');
  };

  const saveRule = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...ruleForm,
        job_id: ruleForm.job_id || null,
        org_unit_id: ruleForm.org_unit_id || null,
        location_id: ruleForm.location_id || null,
        pay_grade_id: ruleForm.pay_grade_id || null,
      };
      if (editingRule === 'new') await api.post(`/employee-benefit-plans/${id}/eligibility-rules`, payload);
      setEditingRule(null);
      load();
    } catch (err) {
      setRuleErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeRule = async (rule: EligibilityRule) => {
    if (!(await confirmDelete('Delete this eligibility rule?'))) return;
    try {
      await api.delete(`/employee-benefit-eligibility-rules/${rule.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!plan) return null;

  return (
    <>
      <header className="page-header">
        <h1>{plan.plan_name}</h1>
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/system/benefit-plans')} />
        </div>
      </header>

      <div className="toolbar">
        <div className="tab-group">
          {TABS.map((t) => (
            <button key={t} type="button" className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
        {tab === 'coverage-options' && (
          <button type="button" className="btn btn-primary" onClick={openNewOption}>
            + Add
          </button>
        )}
        {tab === 'eligibility-rules' && (
          <button type="button" className="btn btn-primary" onClick={openNewRule}>
            + Add
          </button>
        )}
      </div>

      <div className="card">
        {tab === 'details' && (
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-item-label">Plan Code</span>
              <span className="detail-item-value">{plan.plan_code}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Plan Name</span>
              <span className="detail-item-value">{plan.plan_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Type</span>
              <span className="detail-item-value">{plan.type?.type_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Provider</span>
              <span className="detail-item-value">{plan.provider?.provider_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Effective From</span>
              <span className="detail-item-value">{plan.effective_from}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Effective To</span>
              <span className={`detail-item-value${plan.effective_to ? '' : ' empty'}`}>{plan.effective_to ?? 'Not set'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Status</span>
              <span className="detail-item-value">
                <span className={`badge ${plan.active_flag ? 'badge-active' : 'badge-cancelled'}`}>
                  {plan.active_flag ? 'Active' : 'Inactive'}
                </span>
              </span>
            </div>
          </div>
        )}

        {tab === 'coverage-options' && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Coverage Amount</th>
                  <th>Employee Cost</th>
                  <th>Employer Cost</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {plan.coverage_options.map((option) => (
                  <tr key={option.id}>
                    <td className="muted">{option.option_code}</td>
                    <td>{option.option_name}</td>
                    <td className="muted">{option.coverage_amount ? formatMoney(Number(option.coverage_amount)) : '—'}</td>
                    <td>{formatMoney(Number(option.employee_cost))}</td>
                    <td className="muted">{formatMoney(Number(option.employer_cost))}</td>
                    <td className="right">
                      <IconButton icon={IconEdit} label="Edit" onClick={() => openEditOption(option)} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => removeOption(option)} />
                    </td>
                  </tr>
                ))}
                {plan.coverage_options.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted center">
                      No coverage options yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'eligibility-rules' && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Org Unit</th>
                  <th>Location</th>
                  <th>Pay Grade</th>
                  <th>Min. Service (months)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {plan.eligibility_rules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="muted">{rule.job?.job_title ?? 'Any'}</td>
                    <td className="muted">{rule.org_unit?.unit_name ?? 'Any'}</td>
                    <td className="muted">{rule.location?.location_name ?? 'Any'}</td>
                    <td className="muted">{rule.pay_grade?.name ?? 'Any'}</td>
                    <td className="muted">{rule.minimum_service_months ?? '—'}</td>
                    <td className="right">
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => removeRule(rule)} />
                    </td>
                  </tr>
                ))}
                {plan.eligibility_rules.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted center">
                      No eligibility rules yet — this plan is open to everyone.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="muted" style={{ marginTop: 12 }}>
              Eligibility rules are reference information for HR — enrolling an employee does not check them automatically.
            </p>
          </div>
        )}
      </div>

      {editingOption && (
        <Modal title={editingOption === 'new' ? 'Add coverage option' : 'Edit coverage option'} onClose={() => setEditingOption(null)}>
          <form onSubmit={saveOption} className="form-grid">
            {optionErrors.general && <div className="alert alert-error span-2">{optionErrors.general[0]}</div>}
            <label>
              Code *
              <input
                value={optionForm.option_code}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setOptionForm({ ...optionForm, option_code: e.target.value })}
                required
              />
              {optionErrors.option_code && <small className="error">{optionErrors.option_code[0]}</small>}
            </label>
            <label>
              Name *
              <input
                value={optionForm.option_name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setOptionForm({ ...optionForm, option_name: e.target.value })}
                required
              />
              {optionErrors.option_name && <small className="error">{optionErrors.option_name[0]}</small>}
            </label>
            <label>
              Coverage Amount
              <input
                type="number"
                step="0.01"
                min="0"
                value={optionForm.coverage_amount}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setOptionForm({ ...optionForm, coverage_amount: e.target.value })}
              />
              {optionErrors.coverage_amount && <small className="error">{optionErrors.coverage_amount[0]}</small>}
            </label>
            <label>
              Employee Cost *
              <input
                type="number"
                step="0.01"
                min="0"
                value={optionForm.employee_cost}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setOptionForm({ ...optionForm, employee_cost: e.target.value })}
                required
              />
              {optionErrors.employee_cost && <small className="error">{optionErrors.employee_cost[0]}</small>}
            </label>
            <label className="span-2">
              Employer Cost *
              <input
                type="number"
                step="0.01"
                min="0"
                value={optionForm.employer_cost}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setOptionForm({ ...optionForm, employer_cost: e.target.value })}
                required
              />
              {optionErrors.employer_cost && <small className="error">{optionErrors.employer_cost[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingOption(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {editingRule && (
        <Modal title="Add eligibility rule" onClose={() => setEditingRule(null)}>
          <form onSubmit={saveRule} className="form-grid">
            {ruleErrors.general && <div className="alert alert-error span-2">{ruleErrors.general[0]}</div>}
            <label>
              Job
              <select value={ruleForm.job_id} onChange={(e) => setRuleForm({ ...ruleForm, job_id: e.target.value })}>
                <option value="">Any</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.job_title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Org Unit
              <select value={ruleForm.org_unit_id} onChange={(e) => setRuleForm({ ...ruleForm, org_unit_id: e.target.value })}>
                <option value="">Any</option>
                {orgUnits.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.unit_name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Location
              <select value={ruleForm.location_id} onChange={(e) => setRuleForm({ ...ruleForm, location_id: e.target.value })}>
                <option value="">Any</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.location_name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Pay Grade
              <select value={ruleForm.pay_grade_id} onChange={(e) => setRuleForm({ ...ruleForm, pay_grade_id: e.target.value })}>
                <option value="">Any</option>
                {payGrades.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Employment Type
              <input
                value={ruleForm.employment_type}
                onChange={(e) => setRuleForm({ ...ruleForm, employment_type: e.target.value })}
              />
            </label>
            <label>
              Minimum FTE
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={ruleForm.minimum_fte}
                onChange={(e) => setRuleForm({ ...ruleForm, minimum_fte: e.target.value })}
              />
            </label>
            <label>
              Minimum Service (months)
              <input
                type="number"
                min="0"
                value={ruleForm.minimum_service_months}
                onChange={(e) => setRuleForm({ ...ruleForm, minimum_service_months: e.target.value })}
              />
            </label>
            <label>
              Effective From *
              <input
                type="date"
                value={ruleForm.effective_from}
                onChange={(e) => setRuleForm({ ...ruleForm, effective_from: e.target.value })}
                required
              />
              {ruleErrors.effective_from && <small className="error">{ruleErrors.effective_from[0]}</small>}
            </label>
            <label className="span-2">
              Effective To
              <input type="date" value={ruleForm.effective_to} onChange={(e) => setRuleForm({ ...ruleForm, effective_to: e.target.value })} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingRule(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
